/**
 * English strings of Zalo ZNS (Cài đặt › E-mail & thông báo › Zalo ZNS, the branch page of the notify
 * module zalo-zns). The Vietnamese file must have exactly the same keys. Every code the server may show has a
 * name here (`tests/zns-labels.test.mjs` reads the server's lists).
 */
export const zns = {
  page: {
    title: 'Zalo ZNS',
    subtitle: 'Notices to customers\' phones through the registrar\'s Zalo Official Account: which notice uses which Zalo template, messages sent, numbers that receive none.',
    crumb: 'E-mail & notifications',
  },
  overview: {
    title: 'Status',
    off: 'Zalo ZNS is off: no notice goes to Zalo.',
    notConfigured: 'Zalo ZNS is not set up yet (Cài đặt › Module › Thông báo › Zalo ZNS).',
    on: 'On (settings version {{version}})',
    development: 'This is a test server: messages go only to the administrators of the Zalo app and of the Official Account, never to customers.',
    accessUntil: 'Access code valid until',
    renewed: 'Last renewed',
    renewals: 'Renewals',
    quota: 'Messages left today',
    quotaValue: '{{remaining}} of {{daily}}',
    quotaAt: 'as of {{at}}',
    lastProblem: 'Last problem',
    nextTry: 'Next try',
    counts: 'Last 7 days',
    openConnection: 'Connection settings',
    fallbackMinutes: 'An e-mail sent only when Zalo fails waits {{minutes}} minutes for Zalo.',
    chain: 'Access codes',
    noChain: 'Not obtained yet: the first message or the next check gets one.',
    previousChain: 'Still those of the previous settings version (the new one takes over at its first use).',
    lastConnectionProblem: 'Last problem of the connection',
  },
  state: {
    READY: 'Working',
    REFRESH_REFUSED: 'Zalo refused the authorisation code',
    REFRESH_UNKNOWN: 'Zalo did not answer; trying again',
  },
  health: {
    healthy: 'Working',
    degraded: 'Has problems',
    down: 'Not working',
    unknown: 'Not checked yet',
  },
  status: {
    PENDING: 'Waiting',
    SENDING: 'Sending',
    SENT: 'Sent',
    DELIVERED: 'Delivered to the phone',
    FAILED: 'Not sent',
    UNKNOWN: 'Unclear',
    SUPPRESSED: 'Not sent (number refuses Zalo)',
  },
  purpose: { NOTIFICATION: 'Notice', TEST: 'Test' },
  emailMode: {
    ALSO: 'E-mail and Zalo',
    FALLBACK: 'Zalo first, e-mail only if Zalo fails',
  },
  emailModeHint: {
    ALSO: 'The e-mail is always sent; Zalo is an extra.',
    FALLBACK: 'The e-mail waits for Zalo and is sent only when Zalo does not deliver the message.',
    onlyAlso: 'This notice is a legal or evidence notice: its e-mail is always sent.',
  },
  source: {
    recipient_name: 'Recipient name',
    domain: 'Domain',
    domains: 'Domains (first three)',
    expires_on: 'Expiry date',
    days_left: 'Days left',
    years: 'Years',
    expired_on: 'Expired on',
    renew_until: 'Renewable until',
    restore_until: 'Restorable until',
    amount: 'Amount',
    invoice_number: 'Invoice number',
    brand_name: 'Brand name',
    brand_phone: 'Brand hotline',
    brand_website: 'Brand website',
    today: 'Date sent',
    text: 'Fixed text',
  },
  mappingError: {
    EVENT_NOT_ALLOWED: 'This notice never goes to Zalo',
    FALLBACK_NOT_ALLOWED: 'This notice must always be e-mailed',
    TEMPLATE_ID_INVALID: 'Template id: letters and digits only',
    TOO_MANY_PARAMS: 'At most 30 parameters',
    PARAM_NAME_INVALID: 'Parameter name: letters, digits, _ or -',
    SOURCE_NOT_AVAILABLE: 'This notice has no such value',
    SOURCE_UNKNOWN: 'Unknown value',
    FIXED_TEXT_LENGTH: 'Fixed text: 1 to 200 characters',
    FIXED_TEXT_ONE_LINE: 'Fixed text: one line',
    FIXED_TEXT_IDENTIFIER: 'Fixed text may not contain identity, tax or account numbers or e-mail addresses',
    PARAM_VALUE_INVALID: 'Choose a value or type a text',
    PARAM_REQUIRED_BY_PROVIDER: 'Zalo requires this parameter',
    PARAM_NOT_IN_TEMPLATE: 'The Zalo template has no such parameter',
  },
  zaloStatus: {
    ENABLE: 'Approved',
    PENDING_REVIEW: 'Waiting for Zalo review',
    REJECT: 'Rejected by Zalo',
    DISABLE: 'Disabled',
    DELETE: 'Deleted',
  },
  zaloTag: {
    TRANSACTION: 'Transaction',
    IN_TRANSACTION: 'Transaction',
    CUSTOMER_CARE: 'Customer care',
    PROMOTION: 'After-sales',
  },
  /** Codes of the notify branch, of its core and of the Zalo token chain. */
  code: {
    NOT_READY: 'The connection is not ready (no access code yet): the message waits',
    NOT_REACHED: 'The provider could not be reached (tried again)',
    NO_ANSWER: 'No answer from the provider: the message may have arrived; it is not sent again automatically',
    INTERRUPTED: 'Sending was interrupted: the message may have arrived; it is not sent again automatically',
    OPTED_OUT: 'This phone number does not receive these messages',
    PARAMS_INCOMPLETE: 'The notification lacks data for a template parameter',
    DELIVERY_FAILED: 'The provider reported later that the message did not arrive',
    RECIPIENT_INVALID: 'The phone number is not valid',
    RECIPIENT_UNREACHABLE: 'This number cannot receive the message (no Zalo account, inactive, or it refuses this kind of message)',
    TEMPLATE_UNUSABLE: 'The template cannot be used (not approved, disabled or not the OA\'s)',
    PARAMS_INVALID: 'The parameters do not fit the template: correct the template\'s set-up',
    QUIET_HOURS: 'Not sent at night (22:00–06:00); it goes at 06:00',
    TRY_LATER: 'The provider could not take the message now; it is tried again later',
    SENDER_CONFIGURATION: 'The app, the Official Account or their permissions: every message would fail the same way',
    SENDER_BALANCE: 'The sending account\'s balance is too low',
    TEST_MODE_LIMIT: 'A limit of the test mode (only administrators receive messages)',
    REJECTED: 'The provider refused the message',
    NOT_FOUND: 'Not found at the provider',
    ALREADY_EXISTS: 'Already exists at the provider',
    UNAUTHORIZED: 'The provider did not accept the access code; tried again with a new one',
    IP_NOT_ALLOWED: 'The provider does not accept calls from this server\'s address',
    INVALID_REQUEST: 'The provider found the request not valid',
    CONFLICT: 'The provider reported a conflict',
    QUOTA_EXCEEDED: 'A sending limit of the provider is reached',
    INSUFFICIENT_FUNDS: 'The sending account\'s balance is too low',
    RATE_LIMITED: 'Too many requests to the provider; tried again later',
    MAINTENANCE: 'The provider is under maintenance',
    UNSUPPORTED: 'The provider does not support this',
    REFRESHING: 'Another service is getting an access code',
    REFRESH_REFUSED: 'Zalo refused the authorisation code: get a new Refresh Token from Zalo and enter it in the connection\'s settings (Cài đặt › Module › Thông báo)',
    REFRESH_NO_ANSWER: 'No answer from Zalo when getting an access code; tried again',
    REFRESH_WAITING: 'Waiting to get an access code again',
    STATE_UNAVAILABLE: 'Where the access codes are kept could not be read or written',
  },
  /** Zalo's own codes (modules/vendors/zalo-client/src/errors.rs). */
  zaloCode: {
    '-100': 'Zalo had an unknown error; it is tried again later',
    '-101': 'The sending app is not valid',
    '-102': 'The sending app does not exist',
    '-103': 'The app is not activated',
    '-104': "The app's secret key is wrong",
    '-105': 'The app is not linked to an Official Account',
    '-106': 'Zalo does not support this call',
    '-107': 'The message id is not valid',
    '-108': 'The phone number is not valid',
    '-109': 'The template id is not valid',
    '-110': "The recipient's Zalo version is too old",
    '-111': 'The template has no data',
    '-112': "The template's data is not valid",
    '-1121': 'A parameter is longer than the template allows',
    '-1122': 'A template parameter is missing',
    '-1123': 'Zalo could not create the QR code',
    '-1124': 'A parameter has the wrong format',
    '-113': "The template's button is not valid",
    '-1131': "The button's link has the wrong format",
    '-1132': 'The template needs at least one button',
    '-114': 'The user did not receive it (inactive account, messages turned off or an old Zalo version)',
    '-115': 'The Zalo Cloud account has too little balance',
    '-116': 'A parameter value is not valid',
    '-117': 'The OA or app may not use this template',
    '-118': 'The Zalo account does not exist or is disabled',
    '-119': 'The account cannot receive messages',
    '-120': 'The OA may not use this feature (no service plan)',
    '-1201': 'The OA may not create this kind of template',
    '-1202': "The OA may not use the template's images or logo",
    '-121': 'The template has no content',
    '-122': 'The request has the wrong format',
    '-123': 'Zalo could not decrypt the message',
    '-124': 'The access code is not valid',
    '-1241': 'The Zalo app checks appsecret_proof; turn that off',
    '-125': 'The Official Account id is not valid',
    '-126': 'The development-mode wallet has too little balance',
    '-127': 'A template under test can only be sent to administrators',
    '-128': 'The encoding key does not exist',
    '-129': 'Zalo could not create an encryption key; it is tried again later',
    '-130': 'The content is longer than allowed',
    '-131': 'Zalo has not approved the template',
    '-132': 'A parameter is not valid',
    '-133': 'This template may not be sent at night (22:00–06:00); it is sent at 06:00',
    '-134': 'The user has not agreed to receive messages from the OA',
    '-135': 'The OA may not send messages to phone numbers (not verified or on the free plan)',
    '-1351': 'Zalo blocked the OA from sending (violation)',
    '-136': 'The app is not linked to a Zalo Cloud account',
    '-137': 'Paying with the Zalo Cloud account failed (low balance…)',
    '-138': 'The app may not use this feature',
    '-1381': 'The OA has not let the extension use its Zalo Cloud account',
    '-139': 'The user refuses this kind of message',
    '-140': "Under Zalo's sending rules the user cannot receive this kind of message",
    '-141': 'The user refuses messages from the Official Account',
    '-142': 'The RSA key does not exist',
    '-143': 'The RSA key already exists',
    '-144': "The OA's daily sending limit is reached",
    '-1441': "The OA's monthly limit of after-sales messages is reached",
    '-145': 'The OA may not send this kind of content',
    '-146': 'The template was disabled for low sending quality',
    '-147': "The template's daily limit is reached",
    '-1471': 'The monthly limit of after-sales messages to this user is reached',
    '-1472': 'The daily limit of after-sales messages to this user is reached',
    '-148': 'The journey code is missing',
    '-149': 'The journey code is not valid',
    '-1491': "The journey code's type does not fit the template",
    '-150': 'The journey code has expired',
    '-151': 'Not an end-to-end encrypted template',
    '-152': 'Zalo could not get the end-to-end key; it is tried again later',
    '-153': 'The data sent breaks the rules',
    '-158': 'The uploaded file is too large',
    '-159': 'The uploaded file format is not allowed',
    '-160': 'The daily limit of template changes or uploads is reached',
    '-161': 'The sending mode is not valid',
    '-162': 'The over-limit sending mode cannot be used for this kind of message',
    '-249': 'The template cannot be sent to followers',
    'HTTP': 'Zalo answered with an error without a code',
  },
  templates: {
    title: 'Templates',
    intro: 'Each notice can also go to the recipient\'s Zalo with a template Zalo approved for the registrar\'s Official Account. Notices with a one-time link, personal data or third parties never go to Zalo.',
    notice: 'Notice',
    template: 'Zalo template',
    mode: 'E-mail',
    none: 'Not sent to Zalo',
    on: 'On',
    off: 'Off',
    edit: 'Set up',
    never: 'Never sent to Zalo: {{list}}',
    dialogTitle: 'Zalo template for “{{name}}”',
    dialogDescription: 'Zalo\'s template id and what fills each of its parameters. Values come only from the notice; identity papers and personal tax codes are never sent.',
    enabled: 'Send this notice to Zalo',
    templateId: 'Zalo template id',
    templateIdHint: 'As shown in Zalo Cloud (e.g. 295958).',
    load: 'Read the template from Zalo',
    loaded: '{{name}} · {{status}} · {{tag}}{{price}}',
    price: ' · {{price}} đ per message',
    params: 'Parameters',
    paramName: 'Parameter (as named in Zalo)',
    paramValue: 'Value',
    required: 'required',
    maxLength: 'at most {{n}} characters',
    addParam: 'Add a parameter',
    remove: 'Remove',
    fixedText: 'Text',
    save: 'Save',
    saved: 'Saved.',
    delete: 'Stop sending to Zalo',
    deleted: 'This notice no longer goes to Zalo.',
    invalid: 'Some parameters need fixing.',
    notUsable: 'Zalo does not let this template be sent now (not approved or disabled).',
  },
  test: {
    title: 'Send a test',
    phone: 'Phone number',
    phoneHint: 'A Vietnamese mobile number. On a test server only the Zalo app\'s or OA\'s administrators receive it.',
    send: 'Send test',
    sample: 'Sample data of this notice is used.',
    result: 'Result: {{status}}',
  },
  log: {
    title: 'Messages',
    created: 'Created',
    notice: 'Notice',
    phone: 'Phone',
    status: 'Status',
    email: 'E-mail',
    any: 'All',
    search: 'Search',
    empty: 'No message yet.',
    older: 'Older',
    first: 'Newest',
    detailTitle: 'Zalo message',
    detailDescription: 'What was sent to Zalo and what Zalo answered.',
    template: 'Zalo template',
    params: 'Parameters sent',
    tracking: 'Tracking number',
    zaloId: 'Zalo message number',
    sent: 'Sent',
    delivered: 'On the phone',
    attempts: 'Attempts',
    nextAttempt: 'Next try',
    development: 'Test mode (administrators only)',
    resend: 'Send again',
    resendUnknownHint: 'Zalo did not answer this one: send again only if the customer did not receive it.',
    requeued: 'Queued to send again.',
    by: 'By',
    emailStatus: 'E-mail of the notice',
    zaloAnswer: 'Zalo answered',
  },
  optOuts: {
    title: 'Numbers that receive no Zalo message',
    intro: 'Added by staff when a customer asks, or by the system when Zalo says the user refuses messages from the Official Account. E-mails are not affected.',
    phone: 'Phone number',
    reason: 'Reason',
    source: 'Added by',
    added: 'Added on',
    add: 'Add',
    already: 'This number is already on the list.',
    withdraw: 'Remove from the list',
    withdrawn: 'Removed.',
    empty: 'No number.',
    sourceName: { STAFF: 'Staff', CHANNEL: 'Zalo (the user refuses)' },
    everyChannel: 'every channel',
  },
};
